<template>
  <DialogComponent @close="close" v-model="showConfirmationDialog">
    <template v-slot:header>
      {{ $t("titles.confirmMessage") }}
    </template>
    <template v-slot:body>
      <div class="ma-2">
        <h3 style="font-size: 18px">
          {{ $t(active ? "subjectsDeactivate" : "subjectsActivate") }}
        </h3>
      </div>

      <div v-if="checkActiveSubjects()">
        <v-checkbox
          class="mt-1 px-2"
          hide-details="auto"
          color="primary"
          :model-value="selectedSubjects.length === subjects.length"
          @update:model-value="selectAll"
          :label="$t('ALL')"
        />
        <v-checkbox
          v-for="(subject, index) in subjects"
          :key="index"
          class="mt-0 px-2"
          hide-details="auto"
          color="primary"
          v-model="selectedSubjects"
          :value="subject.id"
          :label="subject.name"
        />
      </div>
    </template>
    <template v-slot:actions>
      <v-btn
        id="courseDeactivateDialog-btn-confirm"
        class="mx-1 success"
        :disabled="checkSelected()"
        @click="activateCourse()"
      >
        {{ $t("buttons.confirm") }}
      </v-btn>

      <v-btn
        id="courseDeactivateDialog-btn-cancel"
        class="mx-1"
        color="secondary"
        variant="flat"
        @click="!active ? activateCourse(false) : close()"
      >
        {{ $t("buttons.cancel") }}
      </v-btn>
    </template>
  </DialogComponent>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DialogComponent from '@/components/dialog.component.vue';
import coursesProvider from '@/providers/courses.provider';
import { APPIDS } from '@/enums/';
import { Subject } from '@/types/subject.types';
import { Lang } from '@/types/general.type';
import { useToaster } from '@/composables/useToaster';

export default defineComponent({
  name: 'CourseActivationDialog',
  components: {
    DialogComponent,
  },
  setup() {
    return {
      ...useToaster(),
    };
  },

  props: {
    subjectsToBeActivated: { type: Boolean, required: true },
    active: { type: Boolean, required: true },
    courseId: { type: String, required: true },
  },

  data() {
    return {
      appId: APPIDS.COURSES,
      subjects: [] as Subject[],
      selectedSubjects: [] as string[],
      showConfirmationDialog: false,
    };
  },

  mounted() {
    this.loadSubjects();
  },

  methods: {
    checkActiveSubjects(): boolean {
      return !this.active && this.subjects.length > 0;
    },

    selectAll(checked: boolean | null) {
      this.selectedSubjects = checked
        ? this.subjects.map((c) => c.id ?? '')
        : [];
    },

    checkSelected(): boolean {
      return !this.active && this.selectedSubjects.length === 0;
    },

    getValue(subject: Subject): boolean {
      return subject.id ? this.selectedSubjects.includes(subject.id) : false;
    },

    async loadSubjects() {
      const result = await coursesProvider.getCourseSubjects(
        this.appId,
        this.courseId,
      );
      this.subjects = result.subjects;
      if (this.subjects.length === 0) {
        this.activateCourse();
      } else {
        this.showConfirmationDialog = true;
      }
    },

    async activateCourse(activateSubjects = true) {
      try {
        const selected = this.selectedSubjects.length === this.subjects.length || this.active
          ? []
          : this.selectedSubjects;

        const result = await coursesProvider.courseActivation(
          this.appId,
          this.courseId,
          !this.active,
          selected,
          activateSubjects,
        );

        this.successToaster(result.message);
        this.$emit('activationDone');
      } catch (err) {
        this.errorToaster(err as Lang);
      }
    },

    close() {
      this.$emit('close');
    },
  },
});
</script>
